import { gsap } from 'gsap';


// Utility for toggling aria-expanded attribute

export function toggleAriaExpanded(element, expand) {
    if (expand === undefined) {
        expand = element.getAttribute('aria-expanded') === 'false';
    }

    element.setAttribute('aria-expanded', expand);
}


// Utility for detecting mobile viewports

export function isMobile() {
    return window.matchMedia('(max-width: 60em)').matches;
}


// Utility for initializing dropdowns

let currentDropdown = null;

export function closeCurrentDropdown() {
    if (currentDropdown) {
        currentDropdown.animation.reverse();
        toggleAriaExpanded(currentDropdown.toggle, false);
        currentDropdown = null;
    }
}

export function initializeDropdowns(context = document) {
    const dropdownToggles = context.querySelectorAll('.dropdown-toggle');

    dropdownToggles.forEach(toggle => {
        const dropdown = document.getElementById(toggle.getAttribute('aria-controls'));
        const angle = toggle.querySelector('.dropdown-angle');
        const animation = gsap.timeline({ paused: true, defaults: { duration: 0.3, ease: 'power4.inOut' } });
        const transformOrigin = toggle.dataset.dropdownOrigin || 'top left';

        animation.set(dropdown, { y: -8, scale: 0.9, transformOrigin });
        animation.to(dropdown, { autoAlpha: 1, y: 0, scale: 1 });

        if (angle) {
            animation.to(angle, { rotate: 180 }, 0);
        }

        toggle.addEventListener('click', () => {
            const isExpanded = currentDropdown?.toggle === toggle;
            
            closeCurrentDropdown();

            const beforeOpenEvent = new CustomEvent('dropdown:before-open', { detail: { toggle, dropdown, isExpanded } });
            toggle.dispatchEvent(beforeOpenEvent);
            
            if (!isExpanded) {
                animation.play();
                toggleAriaExpanded(toggle, true);
                currentDropdown = { toggle, animation };
            }
        });
    });

    document.addEventListener('click', (e) => {
        if (currentDropdown && !currentDropdown.toggle.contains(e.target) && !document.getElementById(currentDropdown.toggle.getAttribute('aria-controls')).contains(e.target)) {
            closeCurrentDropdown();
        }
    });

    window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' || e.keyCode === 27) {
            currentDropdown?.toggle.focus();
            closeCurrentDropdown();
        }
    });
}


// Utility for trapping focus within an element
// Based on https://hidde.blog/using-javascript-to-trap-focus-in-an-element/ 

export class FocusTrap {
    constructor(element, focusableEls) {
        this.element = element;
        this.focusableEls = focusableEls ?? this.constructor.getFocusableEls(element);
        this.firstFocusableEl = this.focusableEls[0];
        this.lastFocusableEl = this.focusableEls[this.focusableEls.length - 1];
        this._handler = this._handleKeydown.bind(this);
    }

    static getFocusableEls(element) {
        return element.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])');
    }

    enable() {
        document.addEventListener('keydown', this._handler);
    }

    disable() {
        document.removeEventListener('keydown', this._handler);
    }

    _handleKeydown(e) {
        if (!(e.key === 'Tab' || e.keyCode === 9)) { 
            return; 
        }

        if ( e.shiftKey ) /* shift + tab */ {
            if (document.activeElement === this.firstFocusableEl) {
                this.lastFocusableEl.focus();
                e.preventDefault();
            }
        } else /* tab */ {
            if (document.activeElement === this.lastFocusableEl) {
                this.firstFocusableEl.focus();
                e.preventDefault();
            }
        }
    }
}